import { useState } from "react";
import { Play, X, Sparkles } from "lucide-react";

/**
 * BuddyInMotion — a click-to-play video showcase.
 *
 * Design choices:
 *   • Uses <video preload="none"> so the 60MB MP4 does NOT download on
 *     initial pageload (critical for mobile data users on cellular).
 *   • The clicked state actually mounts the <video> element and starts
 *     playing; the poster state renders a static illustration + play chip.
 *   • On play, the video sits inside a rounded modal-style card with a
 *     Close button, so mobile users can dismiss and continue scrolling.
 *   • Muted-by-default so it can autoplay after the user-initiated click
 *     (iOS Safari rule).
 */
export default function BuddyInMotion() {
  const [playing, setPlaying] = useState(false);

  return (
    <section
      data-testid="section-buddy-motion"
      className="relative py-16 sm:py-24 bg-gradient-to-br from-pb-brand-purple via-[#3A1F6B] to-pb-brand-ink overflow-hidden"
    >
      {/* Supporting Buddy Teal halo — same brand accent as HowItWorks + marquee. */}
      <div
        aria-hidden
        className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle, #06A5A1 0%, transparent 65%)", filter: "blur(40px)" }}
      />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-8 text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-pb-brand-teal/15 px-4 py-1.5 text-[10px] sm:text-xs font-extrabold uppercase tracking-widest text-pb-brand-teal border border-pb-brand-teal/40">
          <Sparkles className="h-3.5 w-3.5" /> Meet a Buddy in motion
        </span>
        <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 sm:mt-5 leading-[1.05] text-white">
          Say hi to <span className="text-pb-brand-orange">Sir AM/PM</span>.
        </h2>
        <p className="mt-3 max-w-lg mx-auto text-white/70 text-base sm:text-lg">
          Every Buddy has a personality. Tap play to watch one come to life —
          this is what dinner starts feeling like.
        </p>

        <div className="mt-8 sm:mt-10">
          {!playing ? (
            <button
              data-testid="btn-play-buddy-motion"
              onClick={() => setPlaying(true)}
              aria-label="Play Sir AM/PM video"
              className="group relative mx-auto w-full max-w-2xl aspect-video rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(28,22,40,0.25)] border-4 border-white cursor-pointer focus:outline-none focus:ring-4 focus:ring-pb-coral/50 bg-white"
            >
              {/* Soft radial spotlight so Sir AM/PM feels like he's on a
               * stage, not floating in a void. Keeps the background clean
               * white so his transparent PNG (eyes, teeth, shield details)
               * reads crisp and doesn't inherit the container's tint. */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(circle at 50% 60%, #FFF6D6 0%, #ffffff 65%, #F5F0E4 100%)",
                }}
              />
              {/* Sir AM/PM character portrait */}
              <img
                src="/buddies/sir-ampm.png"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-contain object-center drop-shadow-[0_12px_28px_rgba(28,22,40,0.15)] transition-transform duration-500 group-hover:scale-105"
                style={{ padding: "6% 8%" }}
              />
              {/* Play chip — bottom-right so it doesn't cover Sir's face */}
              <div className="absolute bottom-4 sm:bottom-6 right-4 sm:right-6 flex items-center gap-2 sm:gap-3">
                <div className="hidden sm:block bg-pb-ink text-white rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest shadow-md">
                  Watch him in action
                </div>
                <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-pb-coral flex items-center justify-center shadow-[0_18px_40px_rgba(242,106,52,0.45)] group-hover:scale-110 transition ring-4 ring-white">
                  <Play className="h-7 w-7 sm:h-8 sm:w-8 text-white ml-1" fill="currentColor" />
                </div>
              </div>
              {/* Mobile-only pill so the CTA copy is still visible on phones */}
              <div className="sm:hidden absolute top-4 left-4 bg-pb-ink text-white rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest shadow-md">
                Watch him in action
              </div>
            </button>
          ) : (
            <div className="relative mx-auto w-full max-w-2xl">
              <video
                data-testid="video-buddy-motion"
                controls
                autoPlay
                muted
                playsInline
                preload="none"
                poster="/buddies/sir-ampm.png"
                className="w-full aspect-video rounded-[2rem] shadow-[0_30px_80px_rgba(28,22,40,0.25)] border-4 border-white bg-white"
              >
                <source
                  src="https://customer-assets-rejwkqb3.emergentagent.net/job_plate-buddies-launch/artifacts/u5p68ab8_Sir%20AMPM-VEED%20%281%29.mp4"
                  type="video/mp4"
                />
                Your browser doesn&apos;t support embedded video.
              </video>
              <button
                data-testid="btn-close-buddy-motion"
                onClick={() => setPlaying(false)}
                aria-label="Close video"
                className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 h-10 w-10 rounded-full bg-white text-pb-ink shadow-[0_8px_20px_rgba(0,0,0,0.25)] flex items-center justify-center border-2 border-pb-ink/10 hover:scale-110 transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
